import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Space,
  Typography,
  Tag,
  List,
  message,
  Divider,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  ThunderboltOutlined,
  RestOutlined,
  SmileOutlined
} from '@ant-design/icons';
import moment from 'moment';
import MomentDatePicker from './MomentDatePicker.tsx';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const PILLAR_OPTIONS = [
  { value: 'Medication', label: 'Medication', icon: <MedicineBoxOutlined />, color: '#1890ff' },
  { value: 'Nutrition', label: 'Nutrition', icon: <HeartOutlined />, color: '#52c41a' },
  { value: 'Movement', label: 'Movement', icon: <ThunderboltOutlined />, color: '#fa8c16' },
  { value: 'Recovery', label: 'Recovery', icon: <RestOutlined />, color: '#722ed1' },
  { value: 'Emotion', label: 'Emotion', icon: <SmileOutlined />, color: '#eb2f96' }
];

const STATUS_OPTIONS = [
  { value: 'Not Started', label: 'Not Started', color: 'default' },
  { value: 'In Progress', label: 'In Progress', color: 'processing' },
  { value: 'Completed', label: 'Completed', color: 'success' },
  { value: 'On Hold', label: 'On Hold', color: 'warning' },
  { value: 'Cancelled', label: 'Cancelled', color: 'error' }
];

const PatientGoalEditForm = ({
  patientGoal,
  onSave,
  onCancel,
  onDelete,
  loading = false,
  totalPatientGoals = 1,
  readOnly = false
}) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({
    smartGoal: '',
    relatedPillar: '',
    tier: 1,
    status: 'Not Started',
    timeline: {
      startDate: moment(),
      targetDate: moment().add(3, 'months'),
      nextReviewDate: moment().add(1, 'month')
    },
    actions: []
  });

  // Initialize form data when patientGoal prop changes
  useEffect(() => {
    if (patientGoal) {
      const initialData = {
        smartGoal: patientGoal.smartGoal || '',
        relatedPillar: patientGoal.relatedPillar || '',
        tier: patientGoal.tier || 1,
        status: patientGoal.status || 'Not Started',
        timeline: {
          startDate: patientGoal.timeline?.startDate ? moment(patientGoal.timeline.startDate) : moment(),
          targetDate: patientGoal.timeline?.targetDate ? moment(patientGoal.timeline.targetDate) : moment().add(3, 'months'),
          nextReviewDate: patientGoal.timeline?.nextReviewDate ? moment(patientGoal.timeline.nextReviewDate) : moment().add(1, 'month')
        },
        actions: patientGoal.actions ? [...patientGoal.actions] : []
      };
      setFormData(initialData);
      
      // Set form values
      form.setFieldsValue({
        smartGoal: initialData.smartGoal,
        relatedPillar: initialData.relatedPillar,
        tier: initialData.tier,
        status: initialData.status
      });
    }
  }, [patientGoal, form]);

  // Update form field
  const updateField = (field, value) => {
    const newData = { ...formData };
    if (field.includes('.')) {
      // Handle nested fields like timeline.startDate
      const [parent, child] = field.split('.');
      newData[parent][child] = value;
    } else {
      newData[field] = value;
    }
    setFormData(newData);
  };



  // Handle form submission
  const handleSubmit = async () => {
    try {
      await form.validateFields();
      
      const updateData = {
        smartGoal: formData.smartGoal,
        relatedPillar: formData.relatedPillar,
        tier: formData.tier,
        status: formData.status,
        timeline: {
          startDate: formData.timeline.startDate.toISOString(),
          targetDate: formData.timeline.targetDate.toISOString(),
          nextReviewDate: formData.timeline.nextReviewDate.toISOString()
        }
        // Note: Actions are managed separately through action endpoints
      };

      await onSave(updateData);
    } catch (error) {
      console.error('Form validation failed:', error);
      message.error('Please check all required fields');
    }
  };

  return (
    <div>
      <Form form={form} layout="vertical" requiredMark={false}>
        {/* Patient Goal Text */}
        <Form.Item
          name="smartGoal"
          label="SMART Goal Text"
          rules={[
            { required: true, message: 'Please enter the SMART goal text' },
            { max: 1000, message: 'Goal text cannot exceed 1000 characters' }
          ]}
        >
          <TextArea
            rows={3}
            placeholder="Enter specific, measurable patient goal..."
            maxLength={1000}
            showCount
            value={formData.smartGoal}
            onChange={(e) => updateField('smartGoal', e.target.value)}
            disabled={readOnly}
          />
        </Form.Item>

        {/* Pillar and Tier Row */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="relatedPillar"
              label="Related Pillar"
              rules={[{ required: true, message: 'Please select a pillar' }]}
            >
              <Select
                placeholder="Select pillar"
                value={formData.relatedPillar}
                onChange={(value) => updateField('relatedPillar', value)}
                style={{ width: '100%' }}
                disabled={readOnly}
              >
                {PILLAR_OPTIONS.map(pillar => (
                  <Option key={pillar.value} value={pillar.value}>
                    <Space>
                      <span style={{ color: pillar.color }}>{pillar.icon}</span>
                      {pillar.label}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="tier"
              label="Difficulty Tier"
              rules={[{ required: true, message: 'Please select a tier' }]}
            >
              <Select
                placeholder="Select tier"
                value={formData.tier}
                onChange={(value) => updateField('tier', value)}
                style={{ width: '100%' }}
                disabled={readOnly}
              >
                <Option value={1}>Tier 1 - Easy (Beginner)</Option>
                <Option value={2}>Tier 2 - Moderate</Option>
                <Option value={3}>Tier 3 - Challenging</Option>
                <Option value={4}>Tier 4 - Difficult (Advanced)</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Status */}
        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: 'Please select a status' }]}
        >
          <Select
            placeholder="Select status"
            value={formData.status}
            onChange={(value) => updateField('status', value)}
            style={{ width: '100%' }}
            disabled={readOnly}
          >
            {STATUS_OPTIONS.map(status => (
              <Option key={status.value} value={status.value}>
                <Tag color={status.color}>{status.label}</Tag>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Timeline Row */}
        <Row gutter={16}>
          <Col span={8}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Start Date</label>
              <MomentDatePicker
                value={formData.timeline.startDate}
                onChange={(date) => updateField('timeline.startDate', date)}
                style={{ width: '100%' }}
                disabled={readOnly}
              />
            </div>
          </Col>
          <Col span={8}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Target Date</label>
              <MomentDatePicker
                value={formData.timeline.targetDate}
                onChange={(date) => updateField('timeline.targetDate', date)}
                style={{ width: '100%' }}
                disabled={readOnly}
              />
            </div>
          </Col>
          <Col span={8}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Next Review</label>
              <MomentDatePicker
                value={formData.timeline.nextReviewDate}
                onChange={(date) => updateField('timeline.nextReviewDate', date)}
                style={{ width: '100%' }}
                disabled={readOnly}
              />
            </div>
          </Col>
        </Row>

        <Divider />

        {/* Actions Section - Read Only */}
        {formData.actions.length > 0 && (
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Action Steps</label>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 12 }}>
              Note: Action management is handled through the Action Management section. This shows current actions for reference only.
            </Text>

            <List
              size="small"
              dataSource={formData.actions}
              renderItem={(action, actionIndex) => (
                <List.Item>
                  <Text>{action.description}</Text>
                  {action.completed && <Tag color="success" style={{ marginLeft: 8 }}>Completed</Tag>}
                </List.Item>
              )}
            />
          </div>
        )}
      </Form>

      {/* Form Actions */}
      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Delete Button - Left Side */}
        {onDelete && totalPatientGoals > 1 && !readOnly && (
          <Popconfirm
            title="Delete Patient Goal"
            description={
              <div>
                <p>Are you sure you want to delete this patient goal?</p>
                <p style={{ marginTop: 8, color: '#ff4d4f', fontWeight: 'bold' }}>
                  This action cannot be undone.
                </p>
              </div>
            }
            onConfirm={() => onDelete(patientGoal._id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okType="danger"
            placement="topLeft"
          >
            <Button danger icon={<DeleteOutlined />} loading={loading}>
              Delete Goal
            </Button>
          </Popconfirm>
        )}

        {/* Save/Cancel Buttons - Right Side */}
        <Space>
          <Button onClick={onCancel}>
            Cancel
          </Button>
          {!readOnly && (
            <Button type="primary" loading={loading} onClick={handleSubmit}>
              Save Changes
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
};

export default PatientGoalEditForm;
